import { Component, inject, signal } from "@angular/core"
import { NgClass } from "@angular/common"
import { Router } from "@angular/router"
import { firstValueFrom } from "rxjs"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { FormField, form, submit } from "@angular/forms/signals"
import { CreateNews, CreateNewsFactory, NEWS_FIELD_LABELS } from "../../models"
import { NewsService, ToastService } from "../../services"
import { TableComponent } from "../../components/table/table.component"
import { invalidFieldsDetail, toServerFieldErrors } from "../../helpers/FormErrors"
import { BooleanInputComponent } from "../../components/boolean-input/boolean-input.component"
import { TextInputComponent } from "../../components/text-input/text-input.component"
import { FileInputComponent } from "../../components/file-input/file-input.component"
import {
    DictionaryInputComponent,
} from "../../components"

@Component({
    selector: "valben-news-add",
    templateUrl: "./news-add.page.html",
    styles: [`
        :host {
            width: 100%;
            flex: 1 1 auto;
            min-height: 0;
            display: flex;
            flex-direction: column;
        }
    `],
    standalone: true,
    imports: [
        NgClass,
        MatProgressSpinnerModule,
        FormField,
        DictionaryInputComponent,
        BooleanInputComponent,
        TextInputComponent,
        FileInputComponent,
    ],
})
export class NewsAddPage {
    private _newsService = inject(NewsService)
    private _messageService = inject(ToastService)
    private _router = inject(Router)

    saving = signal(false)

    model = signal<CreateNews>(CreateNewsFactory.create())
    newsForm = form(this.model)

    save(): void {
        this._create(false)
    }

    draft(): void {
        this._create(true)
    }

    back(): void {
        this._router.navigate(["/news"])
    }

    private async _create(isDraft: boolean): Promise<void> {
        this.newsForm.isDraft().value.set(isDraft)
        this.saving.set(true)
        let failed = false

        const created = await submit(this.newsForm, {
            action: async f => {
                try {
                    await firstValueFrom(this._newsService.Create(f().value()))
                    return null
                } catch (err) {
                    failed = true
                    const { detail, errors } = toServerFieldErrors(err, this.newsForm, NEWS_FIELD_LABELS)
                    this._error(detail)
                    return errors
                }
            },
            onInvalid: () => this._error(invalidFieldsDetail(this.newsForm, NEWS_FIELD_LABELS)),
        })

        this.saving.set(false)
        if (failed || !created) return

        this._messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Uspješno uneseno",
            life: 10000,
        })
        TableComponent.resetPageOffset()
        this.back()
    }

    private _error(detail: string): void {
        this._messageService.add({ severity: "error", summary: "Error", detail, life: 10000 })
    }
}
